import { property, supertypeClass } from '@haventech/amorphic';
import { TermOptions } from '../../js/interfaces/CoverpathTypes';
import { CoverpathBasePolicy } from '../coverpath_subclasses/CoverpathBasePolicy';
import { Policy } from '../../../common/js/customer/Policy';
import { TLICPremiumBreakdown, PremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { MetadataHierarchy } from '../../../common/js/metadata/MetadataHierarchy';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { Product } from '../../../common/js/static/Product';
import { MMDirectCustomer } from './MMDirectCustomer';
import { MMDirectApplication } from './MMDirectApplication';

@supertypeClass({ toClient: true, toServer: true })
export class MMDirectPolicy extends CoverpathBasePolicy {
  @property({ getType: () => MMDirectCustomer })
  customer: MMDirectCustomer;

  @property({
    getType: () => MMDirectApplication,
    comment: 'ref to Application table, which holds application number and policies under that application',
  })
  application: MMDirectApplication;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'determines if the fund for purchasing the policy is withdrawn from retirement account; true means Yes',
  })
  isDOL: boolean;

  @property({
    rule: ['currencyRounded'],
    compositeValidators: ['policyHasValidFaceAmount'],
  })
  faceAmount: number | null;

  @property({
    isLocal: true,
    values: Object.keys(TermOptions),
    descriptions: TermOptions,
    comment: 'Available term options for MMDirectTerm and MMDirectTermECP',
  })
  set termDisplay(value: string) {
    this._termDisplay = value;
    this.term = value ? parseInt(value) : null;

    this.product =
      value && value.match(/ECP/i) ? Product.products.MMDirectTermECP.code : Product.products.MMDirectTerm.code;
  }

  get termDisplay(): string {
    // return null if no term selected(e.g. unsupported usecase)
    if (!this.term) {
      return null;
    }
    if (this.isEcpProduct()) {
      return `${this.term} ECP`;
    } else {
      return this.term.toString();
    }
  }

  @property({
    isLocal: true,
  })
  hasCirRider: boolean = false;

  constructor() {
    super();
  }

  static new(customer, type?) {
    return new MMDirectPolicy().init(customer, type);
  }

  init(customer, type?) {
    super.init(customer, type);

    return this;
  }

  getApplicationHierarchy() {
    const productInfo = this.getProductInfo();
    productInfo.channel = Product.channels.CAS; // Use CAS metadata to build app
    const hierarchy = MetadataHierarchy.build(productInfo, this.getApplicationState());

    // Reset channel, used by workflow emails
    if (this.product === 'ET10') {
      productInfo.channel = Product.channels.LifeBridge;
    } else {
      productInfo.channel = Product.channels.MMDirect;
    }
    return hierarchy;
  }

  getHierarchy(): string[] {
    const hierarchy = super.getHierarchy();
    hierarchy[3] = Product.channels.MMDirect.code;
    return hierarchy;
  }

  getAppQuestionsCriteria() {
    return {
      key: '^.+',
    };
  }

  public getMaxFaceInCurrentStageBy50KIncrements(): number | null {
    // this is used for amendments (currently only Haven)
    // MMDirect will eventually also need this once we have amendments
    return null;
  }

  public updateWithQuoteInfo(quote: BaseQuote): void {
    super.updateWithQuoteInfo(quote);
    this.product = quote.product;
    this.hasEcp = quote.product === Product.products.MMDirectTermECP.code ? true : false;
    this.insured.person.dob = this.customer.currentQuote.dateOfBirth;
  }

  protected updateWithPolicyInfo(policy: Policy): void {
    const createOrUpdate = (pbObject, pbClass) => {
      if (pbObject) {
        pbObject.base = policy.premiumBreakdown.base;
      } else {
        pbObject = pbClass.new(policy.premiumBreakdown.base, null, null, this);
      }

      return pbObject;
    };

    this.faceAmount = policy.faceAmount;
    this.term = policy.term;
    this.paymentFrequency = policy.paymentFrequency;
    this.carrier = 'CAS';
    this.waiver = policy.waiver;
    this.tLICPremiumBreakdown = createOrUpdate(this.tLICPremiumBreakdown, TLICPremiumBreakdown);
    this.finalAlgoPremiumBreakdown = createOrUpdate(this.finalAlgoPremiumBreakdown, PremiumBreakdown);
    this.premiumBreakdown = createOrUpdate(this.premiumBreakdown, PremiumBreakdown);
    const waiverOfPremiumAmount = policy.premiumBreakdown.waiverOfPremiumAmount;
    this.premiumBreakdown.waiverOfPremiumAmount = waiverOfPremiumAmount && waiverOfPremiumAmount.valueOf();
    this.premiumBreakdown.waiver = policy.waiver && waiverOfPremiumAmount ? waiverOfPremiumAmount.valueOf() : 0;
    this.product = policy.product;
    this.hasEcp = policy.product === Product.products.MMDirectTermECP.code ? true : false;
    this.insured.person.dob = policy.insured.person.dob;
  }

  waiverTrigger() {
    // do nothing for MMD.
  }

  // Overriding Policy
  getQuoteHealthClass(): string {
    return this.customer.mmdQuote.healthClass;
  }

  /**
   * An MMD application is never a "tele-CMI app"
   * @returns {boolean}
   */
  isTeleCMIRequired(): boolean {
    return false;
  }
}
